import { FragmentDefiner } from "../../../GlobalHooks/useReadFragment";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { useEffect, useState } from "react";
import { useUser } from "shared-components/Components/User/User.hook";
import { REGISTER_MUTATION, UPDATE_USER_MUTATION, USER_EXISTS_QUERY } from "shared-components/Components/User/User.querys";
import { hasRooms } from "@Utils/Functions";
import { trackNpsLeadSent } from "shared-components/Components/NpsSurveyCard/npsSurvey.service";

export const FRAGMENT_UNITS = new FragmentDefiner(
	"Property",
	`
  project {
    commercial_units {
      id
      code
      link
      title
      bedrooms
      bathrooms
	  m2
	  m2Terrace
      price {
        currency {
          name
        }
        amount
      }
    }
  }
`
);

const SEND_LEAD_MUTATION = gql`
	mutation sendLead($input: LeadInput!) {
		sendLead(input: $input) {
			id
		}
	}
`;

interface ContactInput {
	property_id: number;
	source: number;
	type: string;
	financing_information: boolean;
	message?: string;
	visit_type?: string;
	date?: string;
	utm_args?: UtmArgs;
	name?: string;
	email?: string;
	phone?: string;
	isAutoGenerated?: boolean;
}

interface UtmArgs {
	utm_content?: string | string[];
	utm_source?: string | string[];
	utm_medium?: string | string[];
	utm_campaign?: string | string[];
}

export interface UnitFilter {
	// "beds" -> se agrupa por dormitorios, "rooms" -> por ambientes, "other" -> sin dato
	type: "beds" | "rooms" | "other";
	value: number | null;
	units: any[];
}

/**
 * Agrupa solo por dormitorios. Comportamiento histórico de UY, PY, PE, BO y CL:
 * las tipologías de un proyecto son homogéneas (casas/apartamentos), así que el
 * dormitorio alcanza como criterio. Se mantiene tal cual, incluido el orden.
 */
const buildBedroomFilters = (sortedUnits): UnitFilter[] => {
	const newFilters: UnitFilter[] = [];

	for (const newUnit of sortedUnits) {
		const filterIndex = newFilters.findIndex(filter => filter.value == newUnit.bedrooms);
		if (filterIndex > -1) {
			newFilters[filterIndex].units.push(newUnit);
		} else {
			newFilters.splice(newUnit.bedrooms, 0, {
				type: "beds",
				value: newUnit.bedrooms,
				units: [newUnit],
			});
		}
	}

	return newFilters;
};

/**
 * Devuelve el grupo al que pertenece una unidad replicando lo que muestra la
 * columna Hab/Amb: los tipos de inmueble medidos en ambientes (Oficina, Local,
 * Bodega, Consultorio) se agrupan por `rooms`, el resto por `bedrooms`, y lo que
 * no tiene ninguno de los dos (Lote, Parqueadero) cae en "other".
 */
const getUnitGroup = (unit, country_code): Pick<UnitFilter, "type" | "value"> => {
	if (unit.property_type && hasRooms(unit.property_type.id, country_code)) {
		return unit.rooms > 0
			? { type: "rooms", value: Number(unit.rooms) }
			: { type: "other", value: null };
	}

	return Number.isFinite(Number(unit.bedrooms)) && unit.bedrooms !== null && unit.bedrooms !== ""
		? { type: "beds", value: Number(unit.bedrooms) }
		: { type: "other", value: null };
};

const GROUP_ORDER = { beds: 0, rooms: 1, other: 2 };

/**
 * Agrupa mezclando dormitorios y ambientes. Necesario en CO, donde un mismo
 * proyecto lista tipos de inmueble distintos en la misma tabla.
 */
const buildMixedFilters = (sortedUnits, country_code): UnitFilter[] => {
	const newFilters: UnitFilter[] = [];

	for (const newUnit of sortedUnits) {
		const { type, value } = getUnitGroup(newUnit, country_code);
		const filterIndex = newFilters.findIndex(
			filter => filter.type === type && filter.value === value
		);

		if (filterIndex > -1) {
			newFilters[filterIndex].units.push(newUnit);
		} else {
			newFilters.push({ type, value, units: [newUnit] });
		}
	}

	return newFilters.sort(
		(a, b) => GROUP_ORDER[a.type] - GROUP_ORDER[b.type] || a.value - b.value
	);
};

export const useUnits = ({ comertialUnits, utm = null, country_code = null }) => {
	const [filters, setFilters] = useState([]);
	const [units, setUnits] = useState([]);
	const [sendLeadMutation] = useMutation(SEND_LEAD_MUTATION, {
		onError: error => {
			console.error("Error Ask Availabilty Unit", error);
		},
	});
	const { user, isLoggedIn } = useUser();
	const [
		userExistsCustomQuery,
		{ data: dataUserExistCustom, loading: userExistsCustomLoading },
	] = useLazyQuery(USER_EXISTS_QUERY, {
		fetchPolicy: "network-only",
	});
	
	const [
		registerMutation,
		{ loading: registerLoading, data: dataRegisterMutation },
	] = useMutation(REGISTER_MUTATION);

	const [updateUserMutation, { loading: updateLoading }] = useMutation(UPDATE_USER_MUTATION);
	
	const updateUser = ({ name, phone }) => {
		return updateUserMutation({ variables: { name, phone } });
	};

	const sendLeadTracked = async (variables) => {
		const response = await sendLeadMutation(variables);
		if (response?.data?.sendLead?.id) trackNpsLeadSent();
		return response;
	};

	useEffect(() => {
		if (comertialUnits) {
			const sortedUnits = comertialUnits.slice().sort((a, b) => a.price.amount - b.price.amount);

			setFilters(
				country_code === "CO"
					? buildMixedFilters(sortedUnits, country_code)
					: buildBedroomFilters(sortedUnits)
			);
			setUnits(sortedUnits);
		}
	}, [comertialUnits, country_code]);

	const sendLead = async (unitID: number, extraFields = undefined) => {
		let input: ContactInput = {
			type: "PROPERTY",
			property_id: unitID,
			message: "dispositionID",
			source: 14,
			isAutoGenerated: true,
			financing_information: false,
		};

		if (extraFields) {
			input.name = extraFields.name;
			input.email = extraFields.email;
			input.phone = extraFields.phone;
		}

		if (utm && (utm.utm_campaign || utm.utm_content || utm.utm_medium || utm.utm_source)) {
			input.utm_args = {};
			if (utm.utm_campaign) {
				input.utm_args.utm_campaign = Array.isArray(utm.utm_campaign) ? utm.utm_campaign[0] : utm.utm_campaign.toString();
			}
			if (utm.utm_content) {
				input.utm_args.utm_content = Array.isArray(utm.utm_content) ? utm.utm_content[0] : utm.utm_content.toString();
			}
			if (utm.utm_medium) {
				input.utm_args.utm_medium = Array.isArray(utm.utm_medium) ? utm.utm_medium[0] : utm.utm_medium.toString();
			}
			if (utm.utm_source) {
				input.utm_args.utm_source = Array.isArray(utm.utm_source) ? utm.utm_source[0] : utm.utm_source.toString();
			}
		}

		if (!isLoggedIn) {
			return userExistsCustomQuery({ variables: { email: input.email } }).then(data => {
				if (data?.error?.message?.includes("no existe")) {
					return registerMutation({
						variables: {
							email: input.email,
							name: input.name,
						},
					})
						.then(({ data }) => {
							return updateUser({
								name: input.name,
								phone: input.phone,
							}).then(() => {
								user.refetch();
								return sendLeadTracked({
									variables: {
										input,
									},
								}).then(data => {
									user.refetch();
									return data
								});
							});
						})
						.catch(error => {
							console.error(error);
						});
				} else {
					return sendLeadTracked({
						variables: {
							input,
						},
					});
				}
			});
		}

		return sendLeadTracked({
			variables: {
				input
			},
		});
	};

	return {
		filters,
		units,
		sendLead,
		dataRegisterMutation,
		//this is for refactor when branch is merged
		userExistsCustomQuery,
		registerMutation,
		updateUser,
	};
};
